import _ from 'lodash'
import { EntityState } from '@reduxjs/toolkit'
import { CommonState, Model } from '@/core/classes/Model'
import { SearchRequestOptions, SearchWaitingRoomsQuery } from '@/core/types/request'
import { Consultation } from './consultation.model'
import { Establishment } from './establishment.model'
import { Staff } from './staff.model'
import { SpecialityOption } from './speciality-option.model'
import { SearchResponse } from '../types/response'
import { Network } from './network.model'
import { StaffType } from '@/utils/onboarding/type'

export type CreateWaitingRoomDto = {}

export type UpdateWaitingRoomDto = Partial<CreateWaitingRoomDto>

export type WaitingRoom = {
  _id: string
  avgWaitingTime: number
  allowPatientSelection?: boolean
  consultations: Consultation[]
  createdBy: string
  description?: string
  doctors: string[]
  establishments: Establishment[]
  isPrioritized: boolean
  logoUrl?: string
  name: string
  network?: Network
  occupiedDoctors: string[]
  openingHours: Array<{
    day: number
    periods: Array<{
      open: string
      close: string
    }>
  }>
  specialities: SpecialityOption[]
  price?: number
  paymentReceiverId?: string
  paymentDialogConfig?: {
    showForRoles: StaffType[]
    enabled: boolean
  }
  fairDistribution?: {
    enabled: boolean
    maxConcurrent: number
    cooldownSeconds: number
    // 'cap' = anti-hogging cap + cooldown (default); 'offer' = Uber-style round-robin reservation
    strategy?: 'cap' | 'offer'
    offerTtlSeconds?: number
    prominentOffer?: boolean
  }
  readinessConfig?: {
    enabled: boolean
    prioritizeReady: boolean
    // FormTemplate (category 'readiness-checklist') backing the preparation checklist
    checklistTemplateId?: string
  }
  nurseDoctorExclusions?: { enabled: boolean; rules: Array<{ pdsId: string; doctorIds: string[] }> } | null
  coPresenceConfig?: {
    enabled: boolean
    allowAssign?: boolean
  }
  // Per-consultation checklist progress, attached server-side when the room has a checklist.
  // Broadcasts without this key keep the cached map (top-level Object.assign merge).
  readinessChecklists?: Record<string, ReadinessChecklistSummary>
}

export type WaitingRoomActiveConsultation = {
  _id: string
  patient: string
  medecinId: string | null
  medecin: string
  startedAt: string
  isGroup: boolean
}

type WaitingRoomConsultationPreviewPerson = {
  _id: string | null
  name: string
  surname: string
}

// Read-only pre-acceptance case preview (SEHA), served by
// GET /waiting-rooms/:waitingRoomId/consultations/:consultationId/preview
export type WaitingRoomConsultationPreview = {
  consultationId: string
  status: Consultation['status']
  type: Consultation['type']
  speciality: string | null
  startDate: string
  reason: string | null
  commentaire: string | null
  patient: {
    _id: string | null
    name: string
    surname: string
    birthday: string | null
    gender: string | null
    secu: string | null
    nationality: string | null
  } | null
  pds: WaitingRoomConsultationPreviewPerson | null
  medecin: WaitingRoomConsultationPreviewPerson | null
  createdBy: WaitingRoomConsultationPreviewPerson | null
  mesures: Consultation['mesures']
  documents: Array<{ filename: string; url: string | null; isRecording: boolean }>
  readiness: WaitingRoomConsultationPreviewReadiness | null
}

// Readiness checklist (waiting-room form template) surfaced in the pre-acceptance preview:
// the nurse's preparation status ('submitted' = ready) and the answered required fields.
export type WaitingRoomConsultationPreviewReadiness = {
  submissionId: string | null
  templateId: string
  templateTitle: string
  status: ReadinessChecklistStatus
  submittedAt: string | null
  answered: number
  requiredTotal: number
  completed: number
  total: number
}

export type ReadinessChecklistStatus = 'not-started' | 'draft' | 'submitted'

export type ReadinessChecklistItem = {
  fieldId: string
  label: string
  required?: boolean
  done: boolean
}

export type ReadinessChecklistSummary = {
  submissionId: string | null
  templateId: string
  templateTitle: string
  // The doctor's readiness signal: 'submitted' = operator completed & submitted the preparation form.
  status: ReadinessChecklistStatus
  submittedAt: string | null
  answered: number
  requiredTotal: number
  hasNonBooleanFields: boolean
  // Transitional aliases for the boolean inline flow (ticked / field counts).
  completed: number
  total: number
  items: ReadinessChecklistItem[]
}

export type WaitingRoomFairOffer = {
  consultationId: string
  offeredTo: string
  offerExpiresAt: string
  isMyTurn: boolean
}

export type WaitingRoomFairStatus = {
  enabled: boolean
  strategy: 'cap' | 'offer'
  offerTtlSeconds: number
  maxConcurrent: number
  cooldownSeconds: number
  myActiveCount: number
  cooldownUntil: string | null
  // 'offer' mode: room opted into the prominent your-turn banner
  prominentOffer: boolean
  canAccept: boolean
  reason: 'cooldown' | 'capReached' | null
  // 'offer' mode only: the live reservation of the head patient (null when none is active)
  offer: WaitingRoomFairOffer | null
  // 'offer' mode only: when every online doctor passed, the time the next offer round starts
  nextRoundAt: string | null
}

export type WaitingRoomState = CommonState & {
  waitingRooms: EntityState<WaitingRoom, WaitingRoom['_id']>
}

class WaitingRoomModel extends Model {
  static instance: WaitingRoomModel

  static getInstance() {
    if (!WaitingRoomModel.instance) {
      WaitingRoomModel.instance = new WaitingRoomModel('waitingRoom')
    }
    return WaitingRoomModel.instance
  }

  getInitialState(): WaitingRoomState {
    return _.merge({}, this.commonState, {
      waitingRooms: {
        entities: {},
        ids: [],
      },
    })
  }

  acceptConsultation = async (params: { consultationId: string; doctorId: string; waitingRoomId: string }) => {
    const { consultationId, doctorId, waitingRoomId } = params
    return await this.httpClient.request<{ waitingRoom: WaitingRoom; consultation: Consultation }>(
      `/waiting-rooms/${waitingRoomId}/accept-consultation`,
      'POST',
      { consultationId, doctorId }
    )
  }

  createWaitingRoom = async (createWaitingRoomDto: CreateWaitingRoomDto): Promise<WaitingRoom> => {
    return await this.httpClient.request<WaitingRoom>('/waiting-rooms', 'POST', createWaitingRoomDto)
  }

  getWaitingRoom = async (waitingRoomId: string): Promise<WaitingRoom> => {
    return await this.httpClient.request<WaitingRoom>(`/waiting-rooms/${waitingRoomId}`, 'GET')
  }

  listWaitingRooms = async (query: SearchWaitingRoomsQuery, options?: SearchRequestOptions): Promise<SearchResponse<WaitingRoom>> => {
    const defaultOptions = { limit: 5, order: 'ASC', skip: 0 }
    return await this.httpClient.request<SearchResponse<WaitingRoom>>('/waiting-rooms/query', 'POST', { query, options: _.merge({}, defaultOptions, options) })
  }

  updateWaitingRoom = async (waitingRoomId: string, updateWaitingRoomDto: UpdateWaitingRoomDto): Promise<WaitingRoom> => {
    return await this.httpClient.request<WaitingRoom>(`/waiting-rooms/${waitingRoomId}`, 'PUT', updateWaitingRoomDto)
  }

  deleteWaitingRoom = async (waitingRoomId: string) => {
    await this.httpClient.request(`/waiting-rooms/${waitingRoomId}`, 'DELETE')
  }

  joinWaitingRoom = async (waitingRoomId: string) => {
    return await this.httpClient.request<WaitingRoom>(`/waiting-rooms/${waitingRoomId}/join`, 'PATCH')
  }

  leaveWaitingRoom = async (waitingRoomId: string) => {
    return await this.httpClient.request<WaitingRoom>(`/waiting-rooms/${waitingRoomId}/leave`, 'PATCH')
  }
}

export default WaitingRoomModel.getInstance()
